import React from "react";
import moment from "moment";
import { LuCalendar, LuDatabase, LuPencil, LuTrash2 } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditCategory,
  toggleCategoryModal,
} from "../../features/categories/categoriesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { hasPermission, permission } from "../../static/permission";
import {
  CategoriesProps,
  CategorySortBy,
  CategorySortOrder,
} from "../../types/category";
import { TagIcon } from "../icons";
import Button from "../ui/Button";
import SortableTableHead from "../ui/SortableTableHead";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

type Props = {
  categories: CategoriesProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  // The order the categories were loaded in.
  sortBy: CategorySortBy;
  sortOrder: CategorySortOrder;
  onSort: (sortBy: CategorySortBy, sortOrder: CategorySortOrder) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<CategorySortBy, string>;
  handleDeleteCategory: (id: number) => void;
};

const CategoryList = ({
  categories,
  pageNumber,
  pageSize,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  handleDeleteCategory,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.categoriesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.categoriesDelete
  );

  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 4 + (actionCount > 0 ? 1 : 0);

  const handleEditCategory = async (category: CategoriesProps) => {
    await dispatch(setEditCategory(category));
    dispatch(toggleCategoryModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      handleDeleteCategory(id);
    }
  };

  // The date header shows both dates, so it also stands for the last-changed date the filter can pick.
  const sortHead = (column: CategorySortBy, label: string) => (
    <SortableTableHead
      column={column}
      alsoFor={column === "created_at" ? ["updated_at"] : undefined}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortLabels={sortLabels}
      disabled={isLoading}
    >
      {label}
    </SortableTableHead>
  );

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {sortHead("id", "ક્રમ")}
                  {sortHead("code", "કોડ")}
                  {sortHead("name", "કેટેગરી નામ")}
                  {sortHead("created_at", "તારીખ")}
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : categories.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="કેટેગરી ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {categories.map((category, index) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell className="font-medium">
                        {category.code}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TagIcon className="h-4 w-4 text-gray-400" />
                          <div>
                            <div className="font-medium">{category.name}</div>
                            <div className="text-sm text-gray-500">
                              ID: {category.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(category.created_at)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDate(category.updated_at)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="iconSm"
                                title="સંપાદિત કરો"
                                onClick={() => handleEditCategory(category)}
                              >
                                <LuPencil className="h-4 w-4" />
                              </Button>
                            )}

                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="iconSm"
                                title="કાઢી નાખો"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteRow(category.id)}
                              >
                                <LuTrash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : categories.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="કેટેગરી ની વિગતો મળી નથી" />
          </div>
        ) : (
          categories.map((category, index) => (
            <div key={category.id} className={mobileCardClass}>
              <div className="flex items-center gap-2 border-b p-4">
                <TagIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <div className="font-medium break-words">{category.name}</div>
                  <div className="text-sm text-gray-500">ID: {category.id}</div>
                </div>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>કોડ</span>
                  <span className="font-medium">{category.code}</span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(category.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDate(category.updated_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {canUpdate && (
                    <Button
                      variant="outline"
                      title="સંપાદિત કરો"
                      onClick={() => handleEditCategory(category)}
                    >
                      <LuPencil className="h-4 w-4" />
                    </Button>
                  )}

                  {canDelete && (
                    <Button
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      title="કાઢી નાખો"
                      onClick={() => handleDeleteRow(category.id)}
                    >
                      <LuTrash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default CategoryList;
